import React, {createContext, useContext, useMemo} from 'react';
import {
  IntersectionOptions,
  useInView as rioUseInView,
} from 'react-intersection-observer';

import * as ErrorService from './../../services/errors/errorReporting';

type InViewContextType = {
  entry: IntersectionObserverEntry | undefined;
  isInView: boolean;
};
const InViewContext = createContext<InViewContextType | null>(null);

export const InViewProvider = ({
  children,
  inViewConfig,
  wrapperStyles,
}: {
  children: React.ReactNode;
  inViewConfig?: IntersectionOptions;
  // display:contents will break intersection observer
  wrapperStyles?: React.CSSProperties;
}) => {
  const {ref, inView, entry} = rioUseInView(inViewConfig);

  if (wrapperStyles?.display === 'contents') {
    // this should be a TS rule but IDK how to write it and neither does ChatGPT
    console.error(
      'Can not use display:contents on an element in conjunction with intersection observer'
    );
  }

  const value = useMemo(
    () => ({
      entry,
      isInView: inView,
    }),
    [entry, inView]
  );
  return (
    <InViewContext.Provider value={value}>
      <div data-test="in-view-ref-object" ref={ref} style={wrapperStyles}>
        {children}
      </div>
    </InViewContext.Provider>
  );
};

export const useInView = () => {
  const details = useContext(InViewContext);

  if (details == null) {
    ErrorService.logHandledError(
      'HandledError: useInView used without being inside an InViewProvider - expected currently outside main workspace view'
    );
    return {
      isInView: true,
      entry: undefined,
    };
  }

  return details;
};
